import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Input,
  Result,
  Space,
  Spin,
  Tag,
  Typography,
  message
} from 'antd'
import {
  CameraOutlined,
  CheckCircleOutlined,
  ScanOutlined,
  StopOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import ShopHint from '../../components/shop/ShopHint'
import { useMembership } from '../../hooks/useMembership'
import { usePermission } from '../../hooks/usePermission'
import { getApiErrorMessage } from '../../utils/api'
import { getProductImageUrl } from '../../utils/productImage'
import {
  DELIVERY_METHOD_LABEL,
  PAYMENT_METHOD_LABEL,
  PAYMENT_STATUS_META,
  confirmPickup,
  lookupPickup
} from '../../api/shop'

const { Text, Title } = Typography

/**
 * 到店取貨核銷
 *
 * 兩段式流程：先查詢確認是誰的訂單、買了什麼、付款了沒，再按核銷。
 * 掃到就直接結案的話，掃錯碼或掃到別人的畫面完全沒有攔截點。
 *
 * 取得取貨碼的兩種方式：
 *   1. 掃 QR —— 用瀏覽器原生的 BarcodeDetector。Chrome / Edge / Android
 *      有支援，Safari 沒有，所以只在偵測得到時才顯示掃描按鈕。
 *   2. 手動輸入六位數 —— 任何裝置都能用，也是掃不到時的退路。
 *
 * 另外店家用手機原生相機掃客戶的 QR 會直接開啟這一頁並帶上 ?code=，
 * 那條路完全不需要瀏覽器支援 BarcodeDetector。
 */

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: #f7f8fa;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const Inner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`

const CodeInput = styled(Input)`
  input {
    text-align: center;
    font-size: 30px;
    letter-spacing: 10px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    padding: 10px 0;
  }
`

const VideoBox = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: #000;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 12px;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* 取景框，讓店家知道要把 QR 對到哪裡 */
  &::after {
    content: '';
    position: absolute;
    inset: 18%;
    border: 2px solid rgba(255, 255, 255, 0.85);
    border-radius: 12px;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.28);
  }
`

const ItemRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  img {
    width: 44px;
    height: 44px;
    border-radius: 6px;
    object-fit: cover;
    background: #f5f5f5;
  }

  .info {
    flex: 1;
    min-width: 0;
  }

  .spec {
    font-size: 12px;
    color: #999;
  }
`

/** 從掃到的字串取出六位數取貨碼。可能是網址，也可能只有數字。 */
function extractCode (raw) {
  const text = String(raw || '')
  const fromUrl = text.match(/[?&]code=(\d{6})/)
  if (fromUrl) return fromUrl[1]
  const digits = text.replace(/[^0-9]/g, '')
  if (digits.length === 6) return digits
  // 網址結尾或其他格式，取最後一段連續六位數
  const last = text.match(/(\d{6})(?!.*\d{6})/)
  return last ? last[1] : ''
}

export default function ShopPickupPage () {
  const [searchParams, setSearchParams] = useSearchParams()
  const { hasFeature } = useMembership()
  const { can } = usePermission()

  const canManage = can('shopOrder:manage')
  const planAllowed = hasFeature('shopOrder')

  const [code, setCode] = useState('')
  const [looking, setLooking] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [order, setOrder] = useState(null)
  const [done, setDone] = useState(null)
  const [error, setError] = useState('')

  const [scanning, setScanning] = useState(false)
  // 不只記「能不能掃」，還要記「為什麼不能」——
  // 不支援、沒開 HTTPS、被拒絕權限，這三種的解法完全不同，
  // 一律顯示「瀏覽器不支援」只會讓店家白忙。
  const [scanSupport, setScanSupport] = useState({ ok: false, reason: '' })
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const rafRef = useRef(null)
  const timerRef = useRef(null)
  const viaRef = useRef('manual')

  useEffect(() => {
    if (typeof window === 'undefined') return

    // 相機只在安全來源可用。用 http 連區網 IP 測試時，
    // navigator.mediaDevices 整個不存在，跟瀏覽器支不支援無關。
    if (!window.isSecureContext) {
      setScanSupport({
        ok: false,
        reason:
          '相機功能需要 HTTPS 才能使用。請用正式網址開啟本頁，或改用手動輸入取貨碼。'
      })
      return
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setScanSupport({
        ok: false,
        reason: '這個瀏覽器不支援相機取像，請改用手動輸入取貨碼。'
      })
      return
    }

    setScanSupport({ ok: true, reason: '' })
  }, [])

  const doLookup = useCallback(
    async (value, via = 'manual') => {
      const clean = String(value || '').replace(/[^0-9]/g, '')
      if (clean.length !== 6) {
        setError('請輸入 6 位數取貨碼')
        return
      }
      viaRef.current = via
      setLooking(true)
      setError('')
      setOrder(null)
      setDone(null)
      try {
        const res = await lookupPickup(clean)
        setOrder(res.data.data)
      } catch (e) {
        setError(getApiErrorMessage(e, '查無此取貨碼'))
      } finally {
        setLooking(false)
      }
    },
    []
  )

  /** 關閉相機。忘了關會一直亮著，很多店家會以為是後台在偷錄影。 */
  const stopScan = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    setScanning(false)
  }, [])

  useEffect(() => stopScan, [stopScan])

  /**
   * 取得解碼器。
   *
   * 優先用瀏覽器內建的 BarcodeDetector（Android Chrome、macOS Chrome 有），
   * 那是原生實作，速度最快也最省電。
   * 沒有的話（Safari／所有 iOS 瀏覽器）才動態載入 jsQR 在 JS 裡解 ——
   * 動態載入是刻意的：只有真的按下掃描的人才會下載這包，
   * 不會拖累所有其他後台頁面的載入速度。
   */
  const getDecoder = async () => {
    if (typeof window !== 'undefined' && 'BarcodeDetector' in window) {
      try {
        // eslint-disable-next-line no-undef
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] })
        return async (video) => {
          const codes = await detector.detect(video)
          return codes?.[0]?.rawValue || ''
        }
      } catch (e) {
        // 建構失敗（有些版本宣稱有但不支援 qr_code）就往下用 jsQR
      }
    }

    const mod = await import('jsqr')
    const jsQR = mod.default || mod

    return (video) => {
      const canvas = canvasRef.current
      if (!canvas || !video.videoWidth) return ''
      // 縮到長邊 480 再解：手機相機動輒 1080p，
      // 全解析度逐幀解碼會燙手而且掉禎，對辨識率沒有幫助
      const scale = Math.min(1, 480 / Math.max(video.videoWidth, video.videoHeight))
      const w = Math.round(video.videoWidth * scale)
      const h = Math.round(video.videoHeight * scale)
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      ctx.drawImage(video, 0, 0, w, h)
      const image = ctx.getImageData(0, 0, w, h)
      const result = jsQR(image.data, w, h, { inversionAttempts: 'dontInvert' })
      return result?.data || ''
    }
  }

  const startScan = async () => {
    if (!scanSupport.ok) return
    setError('')

    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      })
    } catch (e) {
      // 分辨「拒絕」與「沒有相機」，兩者的處理方式不同
      const denied =
        e?.name === 'NotAllowedError' || e?.name === 'SecurityError'
      setError(
        denied
          ? '相機權限被拒絕。請到瀏覽器的網站設定允許相機，或改用手動輸入取貨碼。'
          : '無法開啟相機，請改用手動輸入取貨碼。'
      )
      return
    }

    streamRef.current = stream
    setScanning(true)

    let decode
    try {
      decode = await getDecoder()
    } catch (e) {
      stopScan()
      setError('掃碼模組載入失敗，請改用手動輸入取貨碼。')
      return
    }

    // setScanning 之後 video 才會掛上，等一個 frame 再綁 srcObject
    requestAnimationFrame(async () => {
      const video = videoRef.current
      if (!video) return
      video.srcObject = stream
      // iOS 必須 playsInline，否則會被強制全螢幕播放
      video.setAttribute('playsinline', 'true')
      await video.play().catch(() => {})

      const tick = async () => {
        if (!videoRef.current || !streamRef.current) return
        try {
          const raw = await decode(videoRef.current)
          const found = raw ? extractCode(raw) : ''
          if (found) {
            stopScan()
            setCode(found)
            doLookup(found, 'qr')
            return
          }
        } catch (e) {
          // 單張辨識失敗很正常，繼續下一張
        }
        // 每 120ms 解一次就夠了。逐禎解碼在手機上會發燙、掉禎，
        // 反而讓畫面模糊更難掃到。
        timerRef.current = setTimeout(() => {
          rafRef.current = requestAnimationFrame(tick)
        }, 120)
      }
      rafRef.current = requestAnimationFrame(tick)
    })
  }

  // 從客戶 QR 掃進來會帶 ?code=，直接幫店家查好
  useEffect(() => {
    const fromUrl = searchParams.get('code')
    if (fromUrl && canManage && planAllowed) {
      const clean = extractCode(fromUrl)
      if (clean) {
        setCode(clean)
        doLookup(clean, 'qr')
      }
      // 查完就把網址參數清掉，避免重新整理又跳出已核銷的錯誤訊息
      searchParams.delete('code')
      setSearchParams(searchParams, { replace: true })
    }
    // 只在首次掛載時處理
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, planAllowed])

  const handleConfirm = async () => {
    if (!order) return
    setConfirming(true)
    try {
      const res = await confirmPickup(order.pickupCode, viaRef.current)
      setDone(res.data.data)
      setOrder(null)
      setCode('')
      message.success('核銷成功，訂單已完成')
    } catch (e) {
      setError(getApiErrorMessage(e, '核銷失敗'))
    } finally {
      setConfirming(false)
    }
  }

  const reset = () => {
    setDone(null)
    setOrder(null)
    setError('')
    setCode('')
  }

  if (!planAllowed) {
    return (
      <PageWrapper>
        <Inner>
          <Result
            status='info'
            title='線上商店為高階方案功能'
            subTitle='升級後即可使用到店取貨核銷'
          />
        </Inner>
      </PageWrapper>
    )
  }

  if (!canManage) {
    return (
      <PageWrapper>
        <Inner>
          <Result
            status='403'
            title='沒有核銷權限'
            subTitle='請聯繫店長開通「商店訂單管理」權限'
          />
        </Inner>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <Inner>
        <Title level={4} style={{ marginBottom: 4 }}>
          <ScanOutlined /> 到店取貨核銷
        </Title>
        <Text type='secondary'>
          掃描客戶訂單頁的 QR code，或直接輸入畫面上的 6 位數取貨碼
        </Text>

        <Card style={{ marginTop: 16 }}>
          {scanning && (
            <>
              <VideoBox>
                <video ref={videoRef} muted playsInline autoPlay />
                {/* jsQR 解碼用的暫存畫布，不顯示給使用者 */}
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </VideoBox>
              <Button
                block
                icon={<StopOutlined />}
                onClick={stopScan}
                style={{ marginBottom: 16 }}
              >
                停止掃描
              </Button>
            </>
          )}

          {!scanning && scanSupport.ok && (
            <Button
              block
              type='primary'
              size='large'
              icon={<CameraOutlined />}
              onClick={startScan}
              style={{ marginBottom: 16 }}
            >
              掃描 QR code
            </Button>
          )}

          <CodeInput
            size='large'
            maxLength={6}
            inputMode='numeric'
            placeholder='______'
            value={code}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 6)
              setCode(v)
              setError('')
              // 輸滿六碼就自動查，櫃檯不用再多按一次
              if (v.length === 6) doLookup(v, 'manual')
            }}
            onPressEnter={() => doLookup(code, 'manual')}
          />

          <Button
            block
            size='large'
            style={{ marginTop: 12 }}
            loading={looking}
            disabled={code.length !== 6}
            onClick={() => doLookup(code, 'manual')}
          >
            查詢訂單
          </Button>

          {!scanSupport.ok && !scanning && (
            <ShopHint
              tone='info'
              title='目前無法使用相機掃碼'
              style={{ marginTop: 12 }}
            >
              {scanSupport.reason}
            </ShopHint>
          )}

          {error && (
            <ShopHint tone='error' title={error} style={{ marginTop: 12 }} />
          )}
        </Card>

        {looking && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        )}

        {done && (
          <Card style={{ marginTop: 16 }}>
            <Result
              status='success'
              icon={<CheckCircleOutlined />}
              title='核銷成功'
              subTitle={`訂單 ${done.orderNo} 已完成，客戶會收到完成通知`}
              extra={
                <Button type='primary' onClick={reset}>
                  核銷下一筆
                </Button>
              }
            />
          </Card>
        )}

        {order && (
          <Card
            style={{ marginTop: 16 }}
            title={`訂單 ${order.orderNo}`}
            extra={<Tag color='cyan'>{order.statusLabel}</Tag>}
          >
            {order.needsCollectPayment && (
              <ShopHint
                tone='warning'
                title='這筆是貨到付款，交貨前請先向客戶收款'
                style={{ marginBottom: 12 }}
              >
                應收金額 NT$ {Number(order.total || 0).toLocaleString('en-US')}
              </ShopHint>
            )}

            <Descriptions column={1} size='small' bordered>
              <Descriptions.Item label='取貨人'>
                {order.customerName}　{order.customerPhone}
              </Descriptions.Item>
              <Descriptions.Item label='取貨方式'>
                {DELIVERY_METHOD_LABEL.store_pickup}
              </Descriptions.Item>
              <Descriptions.Item label='付款方式'>
                {PAYMENT_METHOD_LABEL[order.paymentMethod]}
                <Tag color={PAYMENT_STATUS_META[order.paymentStatus]?.color}>
                  {PAYMENT_STATUS_META[order.paymentStatus]?.label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label='訂單金額'>
                NT$ {Number(order.total || 0).toLocaleString('en-US')}
              </Descriptions.Item>
              {order.note && (
                <Descriptions.Item label='客戶備註'>
                  {order.note}
                </Descriptions.Item>
              )}
              {order.merchantNote && (
                <Descriptions.Item label='內部備註'>
                  {order.merchantNote}
                </Descriptions.Item>
              )}
            </Descriptions>

            <div style={{ marginTop: 14 }}>
              <Text strong>商品明細</Text>
              <div style={{ marginTop: 8 }}>
                {(order.items || []).map((i, idx) => (
                  <ItemRow key={idx}>
                    {getProductImageUrl(i.productImage)
                      ? (
                        <img
                          src={getProductImageUrl(i.productImage)}
                          alt={i.productTitle}
                        />
                        )
                      : (
                        <div
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 6,
                            background: '#f5f5f5'
                          }}
                        />
                        )}
                    <div className='info'>
                      <div>{i.productTitle}</div>
                      {i.specText && <div className='spec'>{i.specText}</div>}
                    </div>
                    <div>× {i.quantity}</div>
                  </ItemRow>
                ))}
              </div>
            </div>

            <Space style={{ marginTop: 18, width: '100%' }} direction='vertical'>
              <Button
                block
                type='primary'
                size='large'
                loading={confirming}
                icon={<CheckCircleOutlined />}
                onClick={handleConfirm}
              >
                確認交貨並核銷
              </Button>
              <Button block onClick={reset}>
                取消
              </Button>
            </Space>
          </Card>
        )}

        {!order && !done && !looking && !error && (
          <Card style={{ marginTop: 16 }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description='輸入或掃描取貨碼後，這裡會顯示訂單內容'
            />
          </Card>
        )}
      </Inner>
    </PageWrapper>
  )
}
